// 새 오류 코드 공통 처리(계약 SUPPORT_ERROR_CODES·PEP_ERROR_CODES, W6 지시서 E2 표).
// admin-hospital·admin-manufacturer 같은 내용. Vue·axios 를 들이지 않는 순수 모듈(tests/standardErrors.test.mjs).
// axios 응답 가로채기가 standardNotice() 로 문구·다음 동작을 받아 같은 key 로 한 번만 띄운다.

export const STANDARD_MESSAGES = {
  SUPPORT_SCOPE_DENIED: "지원 접속에서는 쓸 수 없는 메뉴입니다", // 계약 SUPPORT 9 화면 약속 문구
  SUPPORT_ACCOUNT_READONLY:
    "서티라이프(리셀러) 지원 계정은 고치거나 지울 수 없습니다.",
  SUPPORT_ACCOUNT_LOGIN_DENIED:
    "지원 계정은 이 화면으로 로그인할 수 없습니다.",
  SUPPORT_CODE_INVALID:
    "접속 코드가 만료되었거나 이미 쓰였습니다. 다시 눌러 주세요.",
  ACCOUNT_INACTIVE: "계정이 정지되었거나 삭제되었습니다.",
  PERM_UNAVAILABLE: "잠시 후 다시 시도해 주세요.",
  AUDIT_UNAVAILABLE:
    "기록 장치 문제로 이 작업을 하지 않았습니다. 잠시 후 다시 시도해 주세요.",
  AUDIT_QUERY_TOO_BROAD: "조건을 좁혀 주세요(기간·회사).",
  TOO_MANY_REQUESTS: "잠시 후 다시 시도해 주세요.",
  LEGACY_ACCESS_BLOCKED:
    "이전 접속 방식은 막혔습니다. 콘솔의 「지원 계정으로 접속」·「서브 관리자로 접속」을 쓰세요.",
};

const codeOf = (error) => error?.response?.data?.code;
const statusOf = (error) => error?.response?.status;

/** 401 SUPPORT_SESSION_ENDED 면 사유(SUPPORT_END_REASONS, 없으면 "ENDED"), 아니면 null */
export const supportEndedReason = (error) =>
  statusOf(error) === 401 && codeOf(error) === "SUPPORT_SESSION_ENDED"
    ? error.response.data.reason || "ENDED"
    : null;

/** 403 PERMISSION_DENIED {required} 의 권한 코드 목록 */
export const requiredCodes = (error) => {
  const r = error?.response?.data?.required;
  return (Array.isArray(r) ? r : r ? [r] : []).filter(
    (c) => typeof c === "string"
  );
};

/** 「권한이 없습니다(필요: …)」. labelOf(code) 는 GET /perm-catalog 이름표(못 받으면 코드 그대로) */
export const permissionDeniedText = (error, labelOf = (c) => c) => {
  // 운영자 전용 코드(ops.*)는 고객사 직원이 받을 수 없어 다른 코드가 있으면 빼고 보임(10-30 I11 「ops.patient.view」)
  const codes = requiredCodes(error);
  const shown = codes.some((c) => !c.startsWith("ops.")) ? codes.filter((c) => !c.startsWith("ops.")) : codes;
  const names = shown.map((c) => labelOf(c) || c);
  return names.length
    ? `권한이 없습니다(필요: ${names.join(", ")})`
    : "권한이 없습니다";
};

/**
 * 오류 응답 → {code, key, level, text, action} 또는 null(이 모듈이 다루지 않는 오류 — 기존 처리 그대로).
 * action: SUPPORT_ENDED(지원 탭 — 갱신하지 않고 끝 화면) · LOGOUT(저장값 지우고 로그인 화면) · RELOAD_PERMS(introspect 한 번 다시) · null
 * 로그인·지원 진입 화면의 401(SUPPORT_ACCOUNT_LOGIN_DENIED·SUPPORT_CODE_INVALID)은 그 화면이 직접 보이므로 여기서 다루지 않는다.
 */
export function standardNotice(
  error,
  { supportTab = false, labelOf, url = error?.config?.url || "" } = {}
) {
  const status = statusOf(error);
  const code = codeOf(error);
  const notice = (level, text, action = null) => ({
    code: code || String(status),
    key: code || String(status),
    level,
    text,
    action,
  });
  if (status === 401) {
    if (code === "SUPPORT_SESSION_ENDED")
      return supportTab ? notice("warning", "", "SUPPORT_ENDED") : null; // 지원 탭이 아니면 일반 401 처리
    if (code === "ACCOUNT_INACTIVE")
      return notice("warning", STANDARD_MESSAGES.ACCOUNT_INACTIVE, "LOGOUT");
    return null;
  }
  if (status === 403) {
    if (code === "PERMISSION_DENIED" || code === "PERM_UNMARKED")
      return notice(
        "warning",
        permissionDeniedText(error, labelOf),
        "RELOAD_PERMS"
      );
    if (code === "SUPPORT_SCOPE_DENIED" || code === "SUPPORT_ACCOUNT_READONLY")
      return notice("warning", STANDARD_MESSAGES[code]);
    if (
      code === "SESSION_NOT_ALLOWED" &&
      error.response.data.reason === "LEGACY_ACCESS_BLOCKED"
    )
      return notice("warning", STANDARD_MESSAGES.LEGACY_ACCESS_BLOCKED);
    return null;
  }
  if (status === 503 && (code === "PERM_UNAVAILABLE" || code === "AUDIT_UNAVAILABLE"))
    return notice("error", STANDARD_MESSAGES[code]);
  if (status === 400 && code === "AUDIT_QUERY_TOO_BROAD")
    return notice("warning", STANDARD_MESSAGES.AUDIT_QUERY_TOO_BROAD);
  // 429 는 이용 내역 조회(동시 2개 상한)만 — 인증번호 요청 제한(OTP_RATE_LIMITED) 등 다른 429 는 각 화면 처리
  if (status === 429 && String(url).includes("/audit/"))
    return notice("warning", STANDARD_MESSAGES.TOO_MANY_REQUESTS);
  return null;
}

// 403 PERMISSION_DENIED 뒤 권한 다시 읽기는 앱이 등록(mfa.setStepUpHandler 와 같은 틀). 동시에 여러 번 와도 한 번만
let permReloadHandler = null;
let permReloading = null;
export const setPermReloadHandler = (handler) => {
  permReloadHandler = handler;
};
export const reloadPermsOnce = () => {
  if (!permReloadHandler) return Promise.resolve();
  if (!permReloading) {
    permReloading = new Promise((resolve) => resolve(permReloadHandler()))
      .catch(() => undefined)
      .finally(() => {
        permReloading = null;
      });
  }
  return permReloading;
};
